import React, { useEffect, useState, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from '@kfc-global/react-shared/redux/Store/hooks';
import { getPWAstorage, setPWAstorage } from '@kfc-global/react-shared/redux/Slices/PwaStorageSlice';
import { RESIZE, OTHER } from 'common/constants/SharedConstants';
import { isMobileDevice, isCurrentPageRedirectFromRNA } from 'common/utilities/utils';
import {
  getBannerPanel,
  getBottomPanel,
  getCardDetailsModal,
  getCustomerPanel,
  getExistingGiftCardPanel,
  getRecipientPanel,
} from './Templates/GiftCardGlobalViewTemplate';
import {
  getGiftCardContentData,
  prepareCustomerFormData,
  prepareRecipientFormData,
  onRecipientFormFieldHandler,
  onCustomerFormElementChangeHandler,
  getCustomerInfoFromRNA,
  defaultGiftCardDetails,
  addRecipientGiftCardPanelHandler,
} from '../Utils/GiftCardUtils';
import { getGiftCardContent } from 'common/utilities/content';
import { contentfulActions, selectContentfulResponse } from '@kfc-global/react-shared/redux/Slices/ContentfulSlice';
import { resetPaymentStatus } from '@kfc-global/react-shared/redux/Actions/PaymentsAction';
import { resetPostOrder } from '@kfc-global/react-shared/redux/Actions/PostOrderAction';

const GiftCardGlobalComponent = props => {
  const { location } = props;
  const dispatch = useAppDispatch();
  const contentfulData = useAppSelector(selectContentfulResponse);
  const { tenantReducer, appStateReducer, profileReducer, paymentsReducer } = useSelector(state => state);
  const getPwaStorageData = useAppSelector(getPWAstorage);
  const [giftCardDetails, setGiftCardDetails] = useState(defaultGiftCardDetails());

  const updateGiftCardDetails = useCallback(param => {
    setGiftCardDetails && setGiftCardDetails(prevState => ({ ...prevState, ...param }));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener(
      RESIZE,
      () => {
        updateGiftCardDetails({ isMobile: isMobileDevice(window.innerWidth) });
      },
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getGiftCardContent());
    dispatch(resetPaymentStatus());
    dispatch(resetPostOrder());
    return () => {
      dispatch(contentfulActions.resetReducer());
    };
  }, [dispatch]);

  useEffect(() => {
    tenantReducer?.tenantId &&
      updateGiftCardDetails({
        tenantId: tenantReducer?.tenantId,
        currencySymbol: tenantReducer?.currencySymbol,
        currencyConversionFactor: tenantReducer?.currencyConversionFactor,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantReducer?.tenantId]);

  useEffect(() => {
    updateGiftCardDetails({ userParams: appStateReducer?.userStatus });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStateReducer?.userStatus]);

  useEffect(() => {
    paymentsReducer?.giftCardPayResponse &&
      updateGiftCardDetails({ giftCardPayResponse: paymentsReducer?.giftCardPayResponse });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsReducer?.giftCardPayResponse, giftCardDetails?.isGoToPaymentButtonClicked]);

  useEffect(() => {
    const customerDetails = {
      customerId: profileReducer?.customerId ?? null,
      customerName: profileReducer?.basic?.firstName ?? '',
      customerLast: profileReducer?.basic?.lastName ?? '',
      customerEmail: profileReducer?.basic?.email ?? '',
      customerPhone: profileReducer?.basic?.phone ?? '',
    };
    if (isCurrentPageRedirectFromRNA(window.location)) {
      const [isloggedInuser, customerName, customerEmail] = getCustomerInfoFromRNA();
      if (isloggedInuser) {
        customerDetails.customerName = customerName;
        customerDetails.customerEmail = customerEmail;
      }
    }

    giftCardDetails?.customerFormRef?.setValueToFormElements('customerName', customerDetails.customerName);
    giftCardDetails?.customerFormRef?.setValueToFormElements('customerEmail', customerDetails.customerEmail);

    !location?.ShowFailureError && !location?.ShowPaymentSuccess &&
      updateGiftCardDetails({
        customerDetails: {
          ...customerDetails,
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    window.location,
    profileReducer?.customerId,
    giftCardDetails?.userParams?.loggedIn,
    profileReducer,
    location?.ShowFailureError,
    location?.ShowPaymentSuccess

  ]);

  useEffect(() => {
    if (!isEmpty(contentfulData?.giftCardContent)) {
      updateGiftCardDetails({
        segmentedGiftCardData: getGiftCardContentData(contentfulData?.giftCardContent?.items?.[0]?.fields),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentfulData?.giftCardContent]);

  useEffect(() => {
    if (!isEmpty(giftCardDetails?.segmentedGiftCardData?.giftCardAmountOptions)) {
      updateGiftCardDetails({
        recipientAmountPanelConfig: {
          ...giftCardDetails?.recipientAmountPanelConfig,
          tileButtonGroupSource: giftCardDetails.segmentedGiftCardData.giftCardAmountOptions.map(option => {
            return {
              value: option === OTHER ? OTHER : option?.split(tenantReducer?.currencySymbol).join('').slice(0, -3),
              label: option,
            };
          }),
          buttonText: giftCardDetails.segmentedGiftCardData.confirmAmountButtonText,
          minimumAmount: giftCardDetails.segmentedGiftCardData?.minAmount,
          maximumAmount: giftCardDetails.segmentedGiftCardData?.maxAmount,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftCardDetails?.segmentedGiftCardData?.giftCardAmountOptions]);

  useEffect(() => {
    if (!isEmpty(giftCardDetails?.recipientAmountPanelConfig?.tileButtonGroupSource)) {
      updateGiftCardDetails({
        customerFormConfig: {
          fieldsConfig: prepareCustomerFormData(contentfulData?.giftCardContent),
          isFormValid: false,
          onFormChangeHandler: onCustomerFormElementChangeHandler,
        },
        recipientFormConfig: {
          fieldsConfig: prepareRecipientFormData({
            giftCardDetails,
            updateGiftCardDetails,
            giftCardContent: contentfulData?.giftCardContent,
            dispatch,
          }),
          isFormValid: false,
          onFormChangeHandler: onRecipientFormFieldHandler,
          name: null,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    giftCardDetails?.recipientAmountPanelConfig?.tileButtonGroupSource,
    giftCardDetails?.recipientDetails,
    window.location,
    giftCardDetails?.userParams?.loggedIn,
  ]);

  useEffect(() => {
    !giftCardDetails?.recipientDetails?.length &&
      contentfulData?.giftCardContent &&
      addRecipientGiftCardPanelHandler(giftCardDetails, updateGiftCardDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentfulData?.giftCardContent, giftCardDetails?.recipientDetails]);

  useEffect(() => {
    if(location?.ShowPaymentSuccess && getPwaStorageData?.loggedIn &&
      getPwaStorageData?.giftCardDataRecipientDetails?.length > 0 &&
      getPwaStorageData?.giftCardDataCustomerDetails){
      const customerDetailsPaymentSuccess = {
        customerName: getPwaStorageData?.giftCardDataCustomerDetails?.customerName ?? '',
        customerEmail: getPwaStorageData?.giftCardDataCustomerDetails?.customerEmail ?? '',
      };
      updateGiftCardDetails({
        customerDetails: {...customerDetailsPaymentSuccess}
      });
    }
    if (
      location?.ShowFailureError &&
      getPwaStorageData?.giftCardDataRecipientDetails?.length > 0 &&
      getPwaStorageData?.giftCardDataCustomerDetails
    ) {
      const customerDetailsPaymentFailure = {
        customerId: getPwaStorageData?.giftCardDataCustomerDetails?.customerId ?? null,
        customerName: getPwaStorageData?.giftCardDataCustomerDetails?.customerName ?? '',
        customerLast: getPwaStorageData?.giftCardDataCustomerDetails?.customerLast ?? '',
        customerEmail: getPwaStorageData?.giftCardDataCustomerDetails?.customerEmail ?? '',
        customerPhone: getPwaStorageData?.giftCardDataCustomerDetails?.customerPhone ?? '',
        isFormValid: getPwaStorageData?.giftCardDataCustomerDetails?.isFormValid ?? '',
      };
      updateGiftCardDetails({
        customerDetails: { ...customerDetailsPaymentFailure },
        recipientDetails: getPwaStorageData?.giftCardDataRecipientDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.ShowFailureError,location?.ShowPaymentSuccess]);

  useEffect(() => {
    giftCardDetails?.tempRecipientDetails?.length &&
      updateGiftCardDetails?.({
        recipientDetails: giftCardDetails?.tempRecipientDetails,
        tempRecipientDetails: [],
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftCardDetails?.tempRecipientDetails]);

  return (
    <div className='giftCard-global-container'>
      {/* Banner Panel */}
      {getBannerPanel(giftCardDetails)}

      {/* Existing Gift Card Panel*/}
      {getExistingGiftCardPanel(giftCardDetails, updateGiftCardDetails, dispatch)}

      {/* Customer Gift card Panel */}
      {getCustomerPanel(giftCardDetails, updateGiftCardDetails)}

      {/* Recipient Gift Card Panel */}
      {getRecipientPanel(giftCardDetails, updateGiftCardDetails)}

      {/* Bottom panel */}
      {getBottomPanel(giftCardDetails, updateGiftCardDetails, dispatch, setPWAstorage)}

      {/* Modal */}
      {getCardDetailsModal(giftCardDetails, updateGiftCardDetails)}
    </div>
  );
};

export default GiftCardGlobalComponent;
